/**
 * Action buttons (save, delete, duplicate) for a SIM level with confirmation dialogs.
 *
 * @module components/sim/createLevels/LevelActions
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    Tooltip
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import React, {useEffect, useState} from "react";
import FileSaver from "file-saver";
import {deleteLevel, deleteScript, downloadExcelLevel, generateAudio} from "../../../services/result.service";
import VolumeUpRoundedIcon from "@material-ui/icons/VolumeUpRounded";
import PropTypes from "prop-types";
import {deleteAudio} from "../../../utils/audioFetch";

/**
 * Represents the actions and state management functionalities for the simulation levels.
 * This variable contains a collection of utilities to handle various level-based operations
 * such as audio generation, deletion of levels, managing state updates, and downloading level data as a file.
 *
 * Parameters:
 * - `classes`: Contains CSS classes or styling information for the component.
 * - `updateData`: Function to update the data of individual simulation scripts.
 * - `data`: Represents the simulation data, including levels and scripts.
 * - `levelIndex`: The index of the currently active level.
 * - `setDeleted`: Function to set the state of deletion operation.
 * - `setData`: Function to update the global simulation data.
 * - `setIndex`: Function to set the currently active level's index.
 * - `setLoad`: Function to manage the loading state during operations.
 * - `load`: Represents the current loading state as either true or false.
 *
 * Functionalities:
 * - Manages the generation of audio assets for the scripts in a level and updates the state accordingly.
 * - Calculates and updates the count of audio scripts in the level.
 * - Enables downloading the level's data as a formatted Excel file.
 * - Handles UI changes and state updates during the deletion of a level.
 *
 * State Management:
 * - `level`: Stores the current level object being processed or displayed.
 * - `deleteLastLevel`: Boolean state indicating whether the last level is being deleted.
 * - `deleteOpen`: Controls the open state of the delete confirmation dialog.
 * - `audioOpen`: Tracks whether the audio generation panel is visible.
 * - `audioCount`: Tracks the total number of scripts in the level requiring audio processing.
 * - `audioProgress`: Indicates the progress of audio asset generation as a percentage.
 *
 * Dependencies:
 * - React hooks for state (`useState`) and side-effects (`useEffect`, `useCallback`).
 * - External services or utilities for operations like audio generation and file downloads.
 */
const LevelActions = ({
    classes,
    updateData,
    data,
    levelIndex,
    setDeleted,
    setData,
    setIndex,
    setLoad,
    load,
    setGeneratingAudio,
}) => {
    const [level, setLevel] = useState(null);
    const [deleteLastLevel, setDeleteLastLevel] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [audioOpen, setAudioOpen] = useState(false);
    const [audioCount, setAudioCount] = React.useState(0);
    const [audioProgress, setAudioProgress] = React.useState(0);

    useEffect(() => {
        setLevel(data?.levels[levelIndex] ?? null);
    }, [data, levelIndex, level, setLevel]);

    /**
     * Handles the generation of audio for a specific level's scripts within the
     *      simulation.
     * Processes each script in the current level, identifies whether the script
     *      requires audio generation, and updates the data with the generated
     *      audio information.
     *
     * This function is wrapped in `React.useCallback` to optimize rendering
     *      performance and prevent unnecessary re-renders.
     *
     * Dependencies:
     * - Relies on external data and several state setters for updating the
     *      simulation's state and tracking the progress of audio generation.
     * - Interacts with an external audio generation service to create audio for
     *      the scripts.
     *
     * Logic:
     * - Iterates over the scripts of the current level (`currentLevel.scripts`).
     * - Determines if a script requires audio generation based on the character
     *      type and certain conditions.
     * - Generates audio using an external service (`generateAudio`) and updates
     *      the script with the returned data.
     * - Adjusts progress indicators based on the number of audios generated.
     *
     * Updates:
     * - Modifies simulation data (`setData`) with new audio information.
     * - Tracks the progress of audio generation (`setAudioProgress`).
     * - Updates the loading state and closes the audio generation panel upon
     *      completion.
     *
     * Dependencies in Closure:
     * - `data`: The current state of the simulation data.
     * - `levelIndex`: Index indicating the active level.
     * - `setAudioOpen`: Function to control the visibility of the audio
     *      generation panel.
     * - `audioCount`: Total number of scripts requiring audio generation.
     * - `setData`: Function to update the state with modified simulation data.
     * - `setLoad`: Function to update the loading state.
     * - `updateData`: Function to manage and save the changes to individual
     *      scripts.
     * - `setAudioProgress`: Function to update the progress of audio generation
     *      as a percentage.
     */
    const handleLevelAudioGenerate = React.useCallback(
        async () => {
            setLoad && setLoad(true);
            const sim = {...data},
                currentLevel = sim.levels[levelIndex];
            let audiosUpdated = 0;
            setGeneratingAudio(true);
            for (let i = 0; i < currentLevel.scripts.length; i++) {
                const currentScript = currentLevel.scripts[i],
                    character = data.sim_characters.find((item) =>
                        item._id === currentScript.sim_character_id
                    );

                if ((character.type === "agent") && !currentScript.agent_recorded_audio) {
                    if (currentLevel.order === 0 || currentLevel.order === 1) {
                        const response = await generateAudio({
                            sim_script_id: currentScript._id,
                            sim_id: currentScript.sim_id,
                            voice: character.voice,
                        });
                        // Clear cached audio blob from IndexedDB so the new audio is fetched
                        await deleteAudio(currentScript._id);
                        currentScript.audio_url = response.audio_url;
                        currentScript.expiry_date = response.expiry_date;
                        currentScript.audio_key = response.audio_key;
                        currentScript.agent_recorded_audio = true;
                        currentScript.voice = character.voice;
                        setData(sim);
                        await updateData(i, currentScript);
                        audiosUpdated++;
                    }
                    // If the character type is 'agent' and enabled to prompt to
                    // record, then do not generate audio for that script
                } else {
                    const response = await generateAudio({
                        sim_script_id: currentScript._id,
                        sim_id: currentScript.sim_id,
                        voice: character.voice,
                    });
                    // Clear cached audio blob from IndexedDB so the new audio is fetched
                    await deleteAudio(currentScript._id);
                    currentScript.audio_url = response.audio_url;
                    currentScript.expiry_date = response.expiry_date;
                    currentScript.audio_key = response.audio_key;
                    currentScript.voice = character.voice;
                    if (currentLevel.order === 0 || currentLevel.order === 1) {
                        currentScript.agent_recorded_audio = true
                    }
                    setData(sim);
                    await updateData(i, currentScript);
                    audiosUpdated++;
                }

                setAudioProgress(audioCount ?
                    (audiosUpdated * 100 / audioCount) :
                    0
                );
            }
            setLoad && setLoad(false);
            setAudioOpen(false);
            setGeneratingAudio(false);
        },
        [
            data, levelIndex, setAudioOpen, audioCount, setData, setLoad,
            updateData, setAudioProgress
        ]
    );

    /**
     * This callback function calculates the count of audio scripts in the current
     *      level and updates the audio count state if necessary. It filters the
     *      provided data for scripts containing a non-empty `audio_url` and
     *      compares the count to the existing audio count.
     *
     * The function relies on the presence of `data`, `levels`, and other
     *      properties, validating their existence before proceeding. If there
     *      are no valid scripts, it performs no updates or actions.
     *
     * Dependencies:
     * - `data`: Represents the core dataset containing levels, their scripts,
     *      and other details.
     * - `levelIndex`: Index of the current level being processed.
     * - `audioCount`: The current count of audio-based scripts.
     * - `setAudioCount`: A function to update the state of the audio count when
     *      necessary.
     */
    const getAudioCount = React.useCallback(
        () => {
            if (!data.levels?.[levelIndex]?.scripts?.length) {
                return;
            }

            const result = data?.levels[levelIndex]?.scripts?.filter(
                (script) => script?.audio_url !== ""
            );
            if (result.length <= 0) {
                //handleLevelAudioGenerate();
            } else if (result.length !== audioCount) {
                setAudioCount(result.length);
            }
        }, [data.levels, levelIndex, audioCount, setAudioCount]
    );

    /**
     * Asynchronously downloads the current level's data as an Excel file.
     *
     * This function checks if the `level` variable is defined. If it exists, it
     *      sends a request to retrieve
     * the level's data in an Excel file format. Upon receiving the response, it
     *      creates a downloadable file using a sanitized version of the level's
     *      name for the file name. The file is saved locally in the `.xlsx`
     *      format.
     *
     * It uses the `downloadExcelLevel` method to fetch the data and the
     *      `FileSaver` library to save the file.
     */
    const downloadLevel = async () => {
        if (!level) {
            return;
        }

        downloadExcelLevel(level._id).then((response) => {
            const fileName = level.name.replace(/\//g, '_');
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
        });
    };

    /**
     * Asynchronous function to handle the deletion of a level in the simulation
     *      data.
     * This function updates the state and manages deletion rules based on the
     *      number of levels present in the simulation data.
     *
     * Behavior:
     * - If the simulation data contains only one level, it triggers a specific
     *      state to indicate the deletion of the last level and opens a deletion
     *      confirmation dialog.
     * - If there are multiple levels, it directly opens the deletion confirmation
     *      dialog.
     */
    const handleDeleteLevel = async () => {
        const sim = {...data};
        if (sim.levels.length === 1) {
            setDeleteLastLevel(true);
            setDeleteOpen(true);
        } else {
            setDeleteOpen(true);
        }
    };

    /**
     * Asynchronously handles the deletion confirmation of a simulation level
     *      and updates relevant state and data.
     *
     * This function performs the following:
     * - Marks the item as deleted by updating the local `deleted` state.
     * - Checks and resets pass/fail criteria settings if enabled in the
     *      simulation data.
     * - Removes the level from the simulation data, either by directly splicing
     *      or via API calls, depending on the state.
     * - Adjusts the current level index if necessary.
     * - Updates the main data state and closes the delete confirmation dialog.
     *
     * Preconditions:
     * - `data` represents the simulation object, containing levels and criteria.
     * - `deleteLastLevel` is a boolean that determines the deletion behavior
     *      for the last level.
     * - `setDeleted`, `setDeleteLastLevel`, `setData`, `setDeleteOpen`, and
     *      `setIndex` are state updater functions.
     * - `deleteScript` and `deleteLevel` are asynchronous functions for API
     *      calls to delete scripts or levels.
     *
     * Postconditions:
     * - The simulation data (`sim`) is updated and saved to reflect the
     *      deletion operation.
     * - UI state is updated appropriately to reflect the changes.
     */
    const deleteConfirm = async () => {
        setDeleted(true);
        const sim = {...data};

        if (sim.is_pass_fail_criteria_set) {
            sim.is_pass_fail_criteria_set = false;
            sim.pass_fail_id = {};
        }

        if (sim.levels[levelIndex]._id === undefined) {
            sim.levels.splice(levelIndex, 1);
        } else if (deleteLastLevel) {
            const data = {};
            data.sim_level_id = sim.levels[levelIndex]._id;
            await deleteScript(data);
            setDeleteLastLevel(false);
            sim.levels[levelIndex].scripts.splice(
                0,
                sim.levels[levelIndex].scripts.length
            );
            setData(sim);
        } else {
            const requestData = {};
            requestData.level_id = sim.levels[levelIndex]._id;
            requestData.sim_id = sim.levels[levelIndex].sim_id;
            await deleteLevel(requestData);
            sim.levels.splice(levelIndex, 1);
        }

        if (levelIndex > 0) {
            setIndex(levelIndex - 1);
        }

        setData(sim);
        setDeleteOpen(false);
    };

    /**
     * Handles the cancellation of a delete operation.
     *
     * This asynchronous function is used to reset specific state variables
     * related to a delete action. It ensures that the delete dialog is closed
     * and any associated flags, like `deleteLastLevel`, are set to their
     * default state.
     */
    const deleteCancel = async () => {
        setDeleteLastLevel(false);
        setDeleteOpen(false);
    };

    /**
     * Event handler for generating audio.
     * This function checks if the current level and its associated scripts
     *      exist in the data object.
     * If valid, it opens the audio modal, fetches the audio count, and sets
     *      the loading state to false.
     *
     * @function
     * @param {Object} evt - The event object triggered by the user's action.
     */
    const handleGenerateAudioClick = (/* evt */) => {
        if (data?.levels[levelIndex] && data?.levels[levelIndex]?.scripts?.length) {
            setAudioOpen(true);
            getAudioCount();
            setLoad(false);
        }
    };

    return (
        <>
            <div>
                <Box className={classes.iconBox} style={{verticalAlign: "middle"}}>
                    <Tooltip title={'Generate Audio for Level ' + level?.name}>
                        <Button className={classes.closeButton} style={{
                            padding: '0.25em',
                            width: 'fit-content',
                            minWidth: 'unset'
                        }}>
                            <VolumeUpRoundedIcon
                                className={classes.iconDownload}
                                onClick={handleGenerateAudioClick}
                            />
                        </Button>
                    </Tooltip>
                    <Tooltip title={'Download level ' + level?.name + ' as a CSV'}>
                        <Button className={classes.closeButton} style={{
                            padding: '0.25em',
                            width: 'fit-content',
                            minWidth: 'unset'
                        }}>
                            <GetAppIcon
                                className={classes.iconDownload}
                                onClick={downloadLevel}
                            />
                        </Button>
                    </Tooltip>
                    <span className={classes.verticalDivider}/>
                    <Tooltip title={"Delete Level " + level?.name}>
                        <Button className={classes.closeButton} style={{
                            padding: '0.25em',
                            width: 'fit-content',
                            minWidth: 'unset'
                        }}>
                            <DeleteOutlineIcon
                                className={classes.iconDelete}
                                onClick={handleDeleteLevel}
                            />
                        </Button>
                    </Tooltip>
                </Box>
            </div>

            <Dialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Delete Level</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {`Do you want to delete the level "${level?.name ?? ''}"`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => deleteCancel()}
                        className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => deleteConfirm()}
                        className={classes.dialogDeleteBtn}
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={audioOpen}
                onClose={() => setAudioOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Generate Audio
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {`There are ${audioCount} audio clips already. Are you sure you want to record over them?`}
                    </DialogContentText>
                    <div className={classes.progress}>
                        {load ?
                            <LinearProgress variant={'determinate'}
                                            value={audioProgress}
                                            color="primary"
                            /> :
                            null
                        }
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAudioOpen(false)}
                            className={classes.cancelButton}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleLevelAudioGenerate()}
                        className={classes.dialogDeleteBtn}
                        autoFocus
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

LevelActions.propTypes = {
    classes: PropTypes.object,
    updateData: PropTypes.func,
    data: PropTypes.object,
    levelIndex: PropTypes.number,
    setDeleted: PropTypes.func,
    setData: PropTypes.func,
    setIndex: PropTypes.func,
    setLoad: PropTypes.func,
    load: PropTypes.bool,
    setGeneratingAudio: PropTypes.func,
};

export default LevelActions;
